import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { STORAGE } from '../constants/local-storage.constants';
import { isPlatformBrowser } from '@angular/common';
import { UserModel } from '@coreModels';

@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {

  /**
   * The authenticated user profile is held in memory only. The access token now lives in a
   * Secure; HttpOnly cookie (invisible to JS) and is never stored here; auth state is rebuilt
   * from GET /oauth/login-user (/me) on app bootstrap.
   */
  private currentUser: UserModel | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

  /**
   * This method is used to get user details (in-memory, populated from /me)
   * @returns user
   */
  getUser(): any {
    return this.currentUser ?? ({} as UserModel);
  }

  /**
   * This method is used to check whether user is logged in or not
   * @returns user is logged in or not
   */
  checkUserLoggedIn(): boolean {
    return this.currentUser !== null;
  }

  /**
   * This method is used to set the authenticated user in memory
   * @param user
   */
  setUser(user: any): void {
    this.currentUser = user ?? null;
    if (this.currentUser) {
      this.setItemStorage(STORAGE.SESSION_HINT_KEY, '1');
    }
  }

  /**
   * This method clears the in-memory authenticated user (logout / 401).
   *
   * Deliberately leaves the session hint alone: a mid-session 401 on some API call only drops the
   * in-memory state, and the next page load re-probes /me exactly as before this hint existed, so a
   * still-valid cookie recovers the session. The hint is cleared only by logout (clearStorage) and
   * by the boot probe itself answering 401 (AuthService.init).
   */
  clearUser(): void {
    this.currentUser = null;
  }

  /**
   * Whether this browser has signed in before and not since logged out. Only a hint: the real
   * session is the HttpOnly cookie, which /me confirms. Used to skip the boot probe for guests.
   */
  hasSessionHint(): boolean {
    return this.getItemStorage(STORAGE.SESSION_HINT_KEY) === '1';
  }

  /**
   * Forget that this browser ever signed in, so the next bootstrap skips the /me probe.
   */
  clearSessionHint(): void {
    this.removeItemStorage(STORAGE.SESSION_HINT_KEY);
  }


  setCreditEarn(credit:any)
  {
    this.setItemStorage(STORAGE.CREDIT_EARN_KEY, JSON.stringify(credit));
  }

  getCreditEarn(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem(STORAGE.CREDIT_EARN_KEY);
    }
    return null; // Return null explicitly when running on the server
  }

  /**
   * This method is used to get data from localStorage by key
   * @param key
   * @returns value or null
   */
  getItemStorage(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem(key);
    }
    return null;
  }

  /**
   * This method is used to set data in localStorage by key
   * @param key
   * @param data
   */
  setItemStorage(key: any, data: any) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(key, data);
    }
  }

  /**
   * This method is used to remove data from localStorage by key
   * @param key
   */
  removeItemStorage(key: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(key);
    }
  }

  setSampleOrder(order: any) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE.SAMPLE_ORDER_KEY, JSON.stringify(order));
    }
  }

  getSampleOrder() {
    if (isPlatformBrowser(this.platformId)) {
      return JSON.parse(localStorage.getItem(STORAGE.SAMPLE_ORDER_KEY) || '[]');
    }
    return null;
  }



  /**
   * This method is used to clear localStorage
   */
  clearStorage(): void {
    this.currentUser = null;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.clear();
    }
  }

  setSearchFields(data: any, key: string = STORAGE.SEARCH_KEY) {
    this.setItemStorage(key, JSON.stringify(data));
  }

  getSearchFields(key: string) {
    if (isPlatformBrowser(this.platformId)) {
      return JSON.parse(this.getItemStorage(key) || '{}');
    }
    return null;
  }

}
